import { StartDatePicker } from "components/StartDatePicker";
import { EndDatePicker } from "components/EndDatePicker";
import { DateRangeSlider } from "components/DateRangeSlider";
import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { OnOffSwitch } from "components/OnOffSwitch";
import { GraphMenuSection } from "components/DataExplorationV2/GraphMenuSection";

export interface XAxisProps {
  /** The status of API fetch for Parameters(Tags) and Machines */
  loading: boolean;
  error: boolean;
  /** Start date of x-axis  */
  startDate: Date | null;
  /** End date of x-axis */
  endDate: Date | null;
  /** Minumum date on x-axis */
  minDate: Date | null;
  /** Maximum date on x-axis  */
  maxDate: Date | null;
  /** X Axis configure switch */
  xAxisLabelEnabled: boolean;

  /**
   * Callback which is fired when the start datepikcer is clicked
   */
  onChangeStartDate: (startDate: Date | null) => void;

  /**
   * Callback which is fired when the end datepikcer is clicked
   */
  onChangeEndDate: (endDate: Date | null) => void;
  /** Callback which is fired when the xAxis ON/OFF switch is clicked*/
  onChangeXAxisLabelEnabled: (newValue: boolean) => void;
  /** Boolean that controls if the section is expanded in configure menu*/
  expanded: boolean;
  /** Callback which is fired when the section accordion is clicked to expand or minimise the section  */
  onToggleExpanded: (expanded: boolean) => void;
}

/** The X Axis section within the Configuration Menu */
export const XAxisSection = ({
  minDate,
  maxDate,
  startDate,
  endDate,
  xAxisLabelEnabled,
  onChangeStartDate,
  onChangeEndDate,
  onChangeXAxisLabelEnabled,
  expanded,
  onToggleExpanded
}: XAxisProps): JSX.Element => {
  return (
    <GraphMenuSection
      title="X Axis"
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
    >
      <SectionWrapper>
        <AxisRangeWrapper>
          <SubHeader variant="caption">Axis Range</SubHeader>
          <DatePickerWrapper>
            <StartDatePicker
              slotProps={{
                popper: {
                  placement: "left-start"
                }
              }}
              disabled={!minDate || !maxDate}
              minDate={minDate}
              maxDate={endDate ?? maxDate}
              startDate={startDate}
              endDate={endDate}
              value={startDate ?? minDate}
              onChange={onChangeStartDate}
              fullWidth
              size="small"
              clearable
            />
          </DatePickerWrapper>
          <DatePickerWrapper>
            <EndDatePicker
              slotProps={{
                popper: {
                  placement: "left-start"
                }
              }}
              disabled={!minDate || !maxDate}
              minDate={startDate ?? minDate}
              maxDate={maxDate}
              startDate={startDate}
              endDate={endDate}
              value={endDate ?? maxDate}
              onChange={onChangeEndDate}
              fullWidth
              size="small"
              clearable
            />
          </DatePickerWrapper>
          <DateRangeSlider
            minDate={minDate}
            maxDate={maxDate}
            startDate={startDate}
            endDate={endDate}
            setSliderStartDate={onChangeStartDate}
            setSliderEndDate={onChangeEndDate}
          />
        </AxisRangeWrapper>
        <OnOffSwitch
          label="Axis Label"
          labelPlacement="start"
          value={xAxisLabelEnabled}
          onChange={onChangeXAxisLabelEnabled}
        />
      </SectionWrapper>
    </GraphMenuSection>
  );
};

const SectionWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const AxisRangeWrapper = styled("div")`
  display: flex;
  flex-direction: column;
`;

const DatePickerWrapper = styled("div")`
  padding: ${({ theme }) => `${theme.spacing(1)} 0px`};
`;

const SubHeader = styled(Typography)`
  padding-bottom: ${({ theme }) => `${theme.spacing(1)} `};
`;
